import React, { useEffect, useState } from 'react';
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import {
  CheckCircleOutline,
  KeyboardArrowDown,
  Poll,
} from '@material-ui/icons';
import _, { Dictionary } from 'lodash';
import Linkify from 'react-linkify';
import { getActivityName, getTimetableName } from '../services/university';
import Paginator from './Paginator';

const perPage = 5;

interface Props {
  results: University[];
  onPageChange: () => void;
}

const useStyles = makeStyles({
  '@global': {
    a: {
      color: '#1D4F91',
    },
  },
  boldText: {
    fontWeight: 'bold',
  },
  expanded: {
    margin: '0 !important',
  },
  expansionContent: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  },
  resultBox: {
    '@global': {
      br: {
        lineHeight: '0.2rem',
      },
    },
  },
  section: {
    alignItems: 'center',
    backgroundColor: '#6F9BD1',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 16px',
  },
});

const ResultBox: React.FC<Props> = ({ results, onPageChange }) => {
  const { resultBox, section } = useStyles();
  const [groups, setGroups] = useState<Dictionary<University[]>>({});
  const [page, setPage] = useState(0);

  useEffect(() => {
    setGroups(_.groupBy(results, 'country'));
    setPage(0);
  }, [results]);

  useEffect(onPageChange, [page]);

  return (
    <div className={resultBox}>
      <Box display="flex" py={1}>
        <Poll color="primary" />
        <Box width={4} />
        <Typography color="primary">Search Result</Typography>
      </Box>
      {Object.keys(groups)
        .slice(page * perPage, (page + 1) * perPage)
        .map((country) => (
          <div key={country}>
            <div className={section}>
              <Typography>{country}</Typography>
              <Typography variant="caption">
                {groups[country].length} Universities
              </Typography>
            </div>
            {groups[country].map((university, index) => (
              <UniversityPanel key={index} university={university} />
            ))}
          </div>
        ))}
      <Paginator
        page={page}
        total={Math.ceil(Object.keys(groups).length / perPage)}
        onPrev={() => setPage(page - 1)}
        onNext={() => setPage(page + 1)}
      />
    </div>
  );
};

const UniversityPanel: React.FC<{ university: University }> = ({
  university,
}) => {
  const { boldText, expanded, expansionContent } = useStyles();
  const linkDecorator = (
    decoratedHref: string,
    decoratedText: string,
    key: number
  ) => (
    <a target="blank" href={decoratedHref} key={key}>
      {decoratedText}
    </a>
  );

  const renderActivityList = () => {
    const active = Object.keys(university.activity).filter((key) => {
      const index = key as keyof typeof university.activity;
      return university.activity[index];
    });

    if (active.length === 0) return <Box>N/A</Box>;

    return active.map((activity) => (
      <Box key={activity} display="flex" alignItems="center">
        <CheckCircleOutline
          fontSize="small"
          style={{ color: '#45C440', marginRight: 4 }}
        />
        <Typography>
          {activity === 'others'
            ? `Others - ${university.activity['others']}`
            : getActivityName(activity)}{' '}
        </Typography>
      </Box>
    ));
  };

  const renderTimetableList = () => {
    const active = Object.keys(university.timetable).filter((key) => {
      const index = key as keyof typeof university.timetable;
      return university.timetable[index];
    });

    if (active.length === 0) return <Box>N/A</Box>;

    return active.map((timetable) => (
      <Typography key={timetable}>- {getTimetableName(timetable)}</Typography>
    ));
  };

  return (
    <Accordion key={university.title} classes={{ expanded }} square>
      <AccordionSummary
        classes={{ content: expansionContent }}
        expandIcon={<KeyboardArrowDown />}
      >
        <Grid container>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="primary">
              {university.country}
            </Typography>
            <Typography className={boldText}>{university.title}</Typography>
            <Typography color="secondary">{university.faculty}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="secondary">
              Activities
            </Typography>
            {renderActivityList()}
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container>
          <Grid item xs={12} md={6}>
            <Box mb={1}>
              <Typography variant="caption" color="primary">
                Summer Program &amp; others
              </Typography>
              <Typography style={{ whiteSpace: 'pre-line' }}>
                {university.program}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="caption" color="primary">
                Approximate fee
              </Typography>
              <Typography style={{ whiteSpace: 'pre-line' }}>
                {university.fee || 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box mb={1}>
              <Typography variant="caption" color="primary">
                Contact
              </Typography>
              <Typography style={{ whiteSpace: 'pre-line' }} noWrap={true}>
                {university.email ? (
                  <Linkify componentDecorator={linkDecorator}>
                    {university.email}
                  </Linkify>
                ) : (
                  'N/A'
                )}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="caption" color="primary">
                Website
              </Typography>
              <Typography style={{ whiteSpace: 'pre-line' }} noWrap={true}>
                {university.website ? (
                  <Linkify componentDecorator={linkDecorator}>
                    {university.website}
                  </Linkify>
                ) : (
                  'N/A'
                )}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="caption" color="primary">
                Attachment
              </Typography>
              <Typography style={{ whiteSpace: 'pre-line' }} noWrap={true}>
                {university.file ? (
                  <Linkify componentDecorator={linkDecorator}>
                    {university.file}
                  </Linkify>
                ) : (
                  'N/A'
                )}
              </Typography>
            </Box>

            <Typography variant="caption" color="primary">
              Timetable
            </Typography>
            {renderTimetableList()}
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default ResultBox;
